import { useLazyQuery } from '@apollo/client';
import { Empty, Select, Spin } from 'antd';
import { debounce, uniqBy } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { LIMIT, SORT } from '../common/constants';
import { CITIES } from './graphql/Queries';
import SelectComponent from './SelectComponent';

const { Option } = Select;

const LOADING_OPTION_KEY = '__loading__';

const CitySelect = (props) => {
  const { isView, stateId, userData, isStateIdChanged, ...rest } = props;
  const [cityList, setCityList] = useState([]);
  const [citiesSearchTerm, setCitiesSearchTerm] = useState('');
  const [isAllCitiesFetched, setIsAllCitiesFetched] = useState(false);
  const [isCitiesAllowClear, setIsCitiesAllowClear] = useState(false);
  const [isCitiesDropdownVisible, setIsCitiesDropdownVisible] = useState(false);
  const [isFetchMoreCitiesLoading, setIsFetchMoreCitiesLoading] = useState(
    false,
  );
  const listRef = useRef(null);
  const isFetchingMoreRef = useRef(false);

  const [cities, { loading: isCitiesLoading }] = useLazyQuery(CITIES, {
    onCompleted: (response) => {
      if (response?.cities?.data?.length < LIMIT) {
        setIsAllCitiesFetched(true);
      }
      isFetchingMoreRef.current = false;
      setIsFetchMoreCitiesLoading(false);
      if (userData?.city && !isStateIdChanged) {
        setCityList(
          uniqBy(
            [{ name: userData?.city }, ...cityList, ...response?.cities?.data],
            'name',
          ),
        );
        return;
      }
      setCityList([...cityList, ...response?.cities?.data]);
    },
    fetchPolicy: 'network-only',
    onError() {
      isFetchingMoreRef.current = false;
      setIsFetchMoreCitiesLoading(false);
    },
  });

  useEffect(() => {
    if (stateId) {
      setCitiesSearchTerm('');
      setCityList([]);
      cities({
        variables: {
          filter: {
            limit: LIMIT,
            skip: 0,
            search: citiesSearchTerm,
            stateName: stateId,
          },
          sort: {
            field: 'name',
            order: SORT?.ASC,
          },
        },
      });
      setIsAllCitiesFetched(false);
    }
  }, [isCitiesDropdownVisible, stateId]);

  const handleCategoriesScroll = (event) => {
    if (!isAllCitiesFetched && !isCitiesLoading && !isFetchingMoreRef.current) {
      const target = event?.target;
      const currentLength = cityList?.length;
      if (
        target?.scrollTop + target?.offsetHeight >=
        target?.scrollHeight - 20
      ) {
        isFetchingMoreRef.current = true;
        setIsFetchMoreCitiesLoading(true);
        cities({
          variables: {
            filter: {
              limit: LIMIT,
              skip: currentLength,
              search: citiesSearchTerm,
              stateName: stateId,
            },
            sort: {
              field: 'name',
              order: SORT?.ASC,
            },
          },
        });
      }
    }
  };

  const handleCategoriesSearch = (searchTerm) => {
    const trimSearch = searchTerm?.trim();
    setIsAllCitiesFetched(false);
    setCityList([]);
    setCitiesSearchTerm(trimSearch);
    cities({
      variables: {
        filter: {
          limit: LIMIT,
          skip: 0,
          search: trimSearch,
          stateName: stateId,
        },
        sort: {
          field: 'name',
          order: SORT?.ASC,
        },
      },
    });
  };

  const handleSearchCategoriesDebounce = debounce(handleCategoriesSearch, 500);

  useEffect(() => {
    handleSearchCategoriesDebounce?.cancel();
  }, [handleSearchCategoriesDebounce]);

  useEffect(() => {
    if (isCitiesLoading) {
      setIsCitiesAllowClear(false);
    } else {
      setIsCitiesAllowClear(true);
    }
  }, [isCitiesLoading]);

  useEffect(() => {
    if (!isFetchMoreCitiesLoading || !listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [isFetchMoreCitiesLoading]);

  return (
    <SelectComponent
      showSearch
      loading={isCitiesLoading && isCitiesDropdownVisible}
      placeholder="Select City"
      optionFilterProp="children"
      allowClear={isCitiesAllowClear}
      onPopupScroll={(event) => {
        listRef.current = event?.target;
        handleCategoriesScroll(event);
      }}
      onSearch={handleSearchCategoriesDebounce}
      onDropdownVisibleChange={(visible) => {
        setIsCitiesDropdownVisible(visible);
      }}
      filterOption={false}
      notFoundContent={
        !isFetchMoreCitiesLoading && isCitiesLoading ? (
          <div className="select-load-more">
            <Spin size="small" />
          </div>
        ) : (
          <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
        )
      }
      disabled={isView || !stateId || userData?.isArchived}
      {...rest}
    >
      {cityList?.map((city) => (
        <Option key={city?.name} value={city?.name}>
          {city?.name}
        </Option>
      ))}
      {isFetchMoreCitiesLoading && (
        <Option key={LOADING_OPTION_KEY} value={LOADING_OPTION_KEY} disabled>
          <div className="select-load-more">
            <Spin size="small" />
          </div>
        </Option>
      )}
    </SelectComponent>
  );
};

export default CitySelect;
